import { useCallback, useEffect, useRef, useState, type Ref } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router'

import { PageHeader } from '../../components/PageHeader'
import { StepRail } from '../../components/capture/StepRail'
import { ChoiceChips } from '../../components/journal/ChoiceChips'
import { Button, Card, Notice, ProblemNotice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { NotFound } from '../NotFound'
import { getRecording, type RecordingRecord } from '../../lib/api'
import { getPlan, getRun, listActivities, postReport, skipReport, type ExperimentPlan, type ExperimentReport as ReportBody, type ExperimentRun } from '../../lib/experiments/api'
import { adoptReportAccount, clearReportDrafts, forgetReportDraft, IN_BETWEEN, keepReportDraft, peekReportDraft, reportAnswers, type ReportDraft } from '../../lib/experiments/reportDraft'
import { deviceLabel } from '../../lib/devices'
import { experimentDate, formatClockTime, formatLocalClock, formatMinutes } from '../../lib/format'
import { ApiError } from '../../lib/problem'
import { CONDITION_WORDS } from '../../lib/recordings'
import { STATE_OPTIONS } from '../../lib/selfReport'
import { useResource } from '../../lib/useResource'
import { useRetryLanding, type ArmLanding } from '../../lib/useRetryLanding'

import '../../styles/experiment-report.css'

const STRESS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => ({ value, label: String(value) }))
const PLANNED = [
  { value: 'as_planned', label: 'Yes' }, { value: 'shorter', label: 'Shorter' },
  { value: 'longer', label: 'Longer' }, { value: 'other', label: 'Something else' },
] as const

/** Mount once with the app so a different account never restores another person's draft. */
export function ExperimentReportDraftBoundary() {
  const { account, isLoading, authError } = useAuth()
  useEffect(() => {
    if (isLoading) return
    if (account) adoptReportAccount(account.user_id)
    // K14 keeps interrupted words for the same account on a sign-in expiry.
    else if (!authError) clearReportDrafts()
  }, [account, authError, isLoading])
  return null
}

export function ExperimentReport() {
  const { runId } = useParams()
  const { account } = useAuth()
  if (!runId || !account) return null
  if (account.experiments_enabled_for_me !== true) return <NotFound />
  return <ReportSession key={`${account.user_id}:${runId}`} id={runId} userId={account.user_id} />
}

export type ExperimentReportContext = { run: ExperimentRun; plan: ExperimentPlan; name: string; limitText?: string | null; before: RecordingRecord | null; after: RecordingRecord | null }
type Operation = { signature: string; key: string; body: ReportBody | { revision: number }; skip: boolean }

function ReportSession({ id, userId }: { id: string; userId: string }) {
  const navigate = useNavigate()
  const [draft, setDraft] = useState(() => peekReportDraft(userId, id))
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<unknown>(null)
  const operation = useRef<Operation | null>(null)
  const pending = useRef<AbortController | null>(null)
  const retryAction = useRef<() => void>(() => undefined)
  const load = useCallback(async (signal: AbortSignal): Promise<ExperimentReportContext> => {
    const run = await getRun(id, signal)
    if (run.id !== id || run.user_id !== userId) throw new Error('The run did not match this account.')
    const [plan, library, before, after] = await Promise.all([
      getPlan(run.plan_id, signal), listActivities(signal),
      run.before_recording_id ? getRecording(run.before_recording_id, { signal }) : null,
      run.after_recording_id ? getRecording(run.after_recording_id, { signal }) : null,
    ])
    if (plan.user_id !== userId || plan.id !== run.plan_id || plan.activity_key !== run.activity_key) throw new Error('The plan did not match this account.')
    for (const [record, recordingId] of [[before, run.before_recording_id], [after, run.after_recording_id]] as const) {
      if (record && (record.recording_id !== recordingId || record.user_id !== userId)) throw new Error('The reading did not match this run.')
    }
    const activity = library.library_version === plan.library_version
      ? library.activities.find((item) => item.key === run.activity_key && item.library_version === plan.library_version) : undefined
    if (run.activity_key === 'coffee' && !activity?.limit_text) throw new Error('The coffee limits for this saved experiment are unavailable. Recheck the saved activity context.')
    // A current library cannot replace a saved protocol. Its keys remain exact
    // if that historical display name is unavailable (EXP-007-C2).
    return { run, plan, name: plan.custom_name ?? activity?.name ?? run.activity_key, limitText: activity?.limit_text ?? null, before, after }
  }, [id, userId])
  const read = useResource(load)
  const [heading, armLanding] = useRetryLanding(read.error !== null || failure !== null)

  useEffect(() => () => pending.current?.abort(), [])

  const edit = (next: ReportDraft) => {
    setDraft(next)
    keepReportDraft(userId, id, next)
    setFailure(null)
  }

  const submit = async (skip: boolean) => {
    const context = read.data
    if (pending.current || !context || context.run.report_state !== 'pending') return
    const answer = skip ? { revision: context.run.revision } : reportAnswers(draft, context.run.revision)
    if (!answer) return
    const signature = JSON.stringify({ skip, answer })
    const previous = operation.current
    const next: Operation = previous?.signature === signature ? previous : {
      signature, key: crypto.randomUUID(), skip,
      body: skip ? answer : { ...answer, answered_at: new Date().toISOString() } as ReportBody,
    }
    operation.current = next
    retryAction.current = () => { void submit(skip) }
    const controller = new AbortController()
    pending.current = controller
    setBusy(true)
    setFailure(null)
    try {
      const saved = skip ? await skipReport(id, next.body.revision, next.key, controller.signal)
        : await postReport(id, next.body as ReportBody, next.key, controller.signal)
      controller.signal.throwIfAborted()
      if (saved.id !== id || saved.user_id !== userId || saved.report_state !== (skip ? 'skipped' : 'answered')) {
        throw new Error('The report was not confirmed for this run.')
      }
      forgetReportDraft(userId, id)
      void navigate(`/experiment-runs/${encodeURIComponent(id)}`, { replace: true })
    } catch (error) {
      if (!controller.signal.aborted) setFailure(error)
    } finally {
      if (!controller.signal.aborted) { pending.current = null; setBusy(false) }
    }
  }

  const context = read.data
  const header = <ReportHeader context={context} headingRef={heading} />

  if (read.error !== null || read.isLoading || context === null) return <div className="stack stack--wide experiment-report">
    {header}
    {read.error !== null ? <ProblemNotice error={read.error} onRetry={read.reload} retryBusy={read.isLoading} landing={armLanding}>The experiment could not be loaded.</ProblemNotice>
      : <p role="status">Loading the experiment…</p>}
  </div>
  const { run } = context
  if (run.report_state !== 'pending') return <Navigate to={`/experiment-runs/${encodeURIComponent(id)}`} replace />
  if (run.stage !== 'awaiting_report' || !run.before_recording_id || !run.after_recording_id) return <div className="stack stack--wide experiment-report">
    {header}<Notice><p>{run.stage}</p><Link to={`/record/experiment/${encodeURIComponent(id)}`}>Open the run</Link></Notice>
  </div>

  const stale = failure instanceof ApiError && failure.code === 'experiment.stale_revision'
  return <ExperimentReportContent context={context} draft={draft} busy={busy} failure={failure}
    onChange={edit} onSave={() => void submit(false)} onSkip={() => void submit(true)}
    onRetry={stale ? read.reload : () => retryAction.current()} headingRef={heading} retryLanding={armLanding} />
}

function ReportHeader({ context, headingRef }: { context: ExperimentReportContext | null; headingRef?: Ref<HTMLElement> }) {
  return <PageHeader title="Before you see the result" ref={headingRef} tabIndex={-1}
    eyebrow={context ? `Experiments · ${context.name} · ${experimentDate(context.before?.local_date ?? context.before?.session_date ?? context.run.created_at.slice(0, 10))}` : 'Experiments'}
    lead="Both readings are kept. Say how you feel now, then the result opens. It stays closed until you answer or skip, so what you see cannot shape what you say." />
}

/** The full report render has no reads, thresholds or defaults for unanswered questions. */
export function ExperimentReportContent({ context, draft, busy = false, failure = null, onChange, onSave, onSkip, onRetry, headingRef, retryLanding }: {
  context: ExperimentReportContext
  draft: ReportDraft
  busy?: boolean
  failure?: unknown
  onChange: (next: ReportDraft) => void
  onSave: () => void
  onSkip: () => void
  onRetry?: () => void
  headingRef?: Ref<HTMLElement>
  retryLanding?: ArmLanding
}) {
  const { run, plan, name, before, after } = context
  const time = (record: RecordingRecord | null) => formatLocalClock(record?.start_local_time)
  const toggle = (value: ReportBody['in_between'][number]) => {
    const selected = draft.between.includes(value)
    const values = selected ? draft.between.filter((item) => item !== value)
      : value === 'nothing' ? [value] : [...draft.between.filter((item) => item !== 'nothing'), value]
    onChange({ ...draft, between: IN_BETWEEN.filter((item) => values.includes(item.value)).map((item) => item.value) })
  }

  return <div className="stack stack--wide experiment-report" data-testid="experiment-report-screen">
    <ReportHeader context={context} headingRef={headingRef} />
    {context.limitText && <Notice><p>{context.limitText}</p></Notice>}
    <div className="experiment-report__rail"><StepRail steps={[
      { n: '✓', label: `Before · ${time(before)} · kept`, state: 'done' },
      { n: '✓', label: `${name} · ${formatClockTime(run.activity_started_at)}–${formatClockTime(run.activity_finished_at)}`, state: 'done' },
      { n: '✓', label: `Settle · ${formatMinutes(plan.settle_s)}`, state: 'done' },
      { n: '✓', label: `After · ${time(after)} · kept`, state: 'done' },
      { n: '5', label: 'How you feel', state: 'now' }, { n: '6', label: 'Result · closed', state: 'pending' },
    ]} /></div>
    <div className="experiment-report__layout">
      <Card as="section" active className="experiment-report__sheet" aria-label="Your report before the result">
        <form onSubmit={(event) => { event.preventDefault(); onSave() }}>
          <fieldset disabled={busy} className="experiment-report__fields">
            <legend className="visually-hidden">How you feel</legend>
            <div className="experiment-report__question"><span className="experiment-report__anchor">Right now</span>
              <ChoiceChips label="How do you feel?" options={STATE_OPTIONS} value={draft.state} onChange={(state) => onChange({ ...draft, state })} />
            </div>
            <div className="experiment-report__question experiment-report__stress"><span className="experiment-report__anchor">Right now</span>
              <ChoiceChips label="Stress, 1 to 10" layout="scale" options={STRESS} value={draft.stress} onChange={(stress) => onChange({ ...draft, stress })} />
            </div>
            <div className="experiment-report__question"><span className="experiment-report__anchor">{run.activity_key === 'short_walk' ? 'The walk' : name}</span>
              <ChoiceChips label="Did it go as planned?" options={PLANNED} value={draft.planned} onChange={(planned) => onChange({ ...draft, planned })} />
            </div>
            <div className="experiment-report__question"><span className="experiment-report__anchor">Since the first reading</span>
              <span className="field__label" id="experiment-between-label">Anything else in between?</span>
              <div className="chipset" role="group" aria-labelledby="experiment-between-label">
                {IN_BETWEEN.map(({ value, label }) => <button type="button" className="chip" key={value} aria-pressed={draft.between.includes(value)} onClick={() => toggle(value)}>{label}</button>)}
              </div>
            </div>
          </fieldset>
          {failure !== null && <ProblemNotice error={failure} onRetry={onRetry} retryBusy={busy} landing={retryLanding}>The report could not be saved.</ProblemNotice>}
          <div className="experiment-report__actions">
            <Button type="submit" busy={busy} disabled={draft.planned === null}>Save and open the result</Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={onSkip}>Skip</Button>
            <p>Skipped is stored as skipped, never guessed.</p>
          </div>
        </form>
      </Card>
      <aside className="experiment-report__explainers" aria-label="About this report">
        <Card as="section" eyebrow="What was captured">
          <dl className="experiment-report__facts">
            <dt>Before</dt><dd>{time(before)} · {before?.status ?? '—'}</dd>
            <dt>After</dt><dd>{time(after)} · {after?.status ?? '—'}</dd>
            <dt>Headband</dt><dd>{deviceLabel(before?.device_id) ?? before?.device_id ?? '—'} / {deviceLabel(after?.device_id) ?? after?.device_id ?? '—'}</dd>
            <dt>Condition</dt><dd>{before ? CONDITION_WORDS[before.condition] : '—'} / {after ? CONDITION_WORDS[after.condition] : '—'}</dd>
          </dl>
          <p>Both readings were self-checked and sent. Neither enters your baseline.</p>
        </Card>
        <Card as="section" flat eyebrow="Why the result waits"><p>Your answer here is the only record of how you felt that the result cannot have influenced. It is stored with the time it was given and the fact that no result had been served yet. The same question after the reveal would be a different signal, and is kept apart.</p></Card>
        <Card as="section" flat eyebrow="If a reading had been lost"><p>A lost contact in the after reading keeps the before reading and offers one more try within the settle window; outside it, the run is kept as <em>incomplete</em> in your playbook, and nothing is compared.</p></Card>
      </aside>
    </div>
  </div>
}
